import { inject, Injectable } from '@angular/core';
import { BrowserStorageService } from 'common/common/browser-storage.service';

/** Checks whether a parsed value is a non-array object suitable for profile settings. */
function isSettingsObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

@Injectable({ providedIn: 'root' })
export class ProfileSettingsService {
  private static readonly STORAGE_KEY = 'companion.profile-settings';
  private readonly storage = inject(BrowserStorageService);

  /** Returns the saved language, or null when no valid language setting is stored. */
  getLanguage(): string | null {
    const language = this.readSettings()['language'];
    return typeof language === 'string' ? language : null;
  }

  /** Saves the selected language while preserving other profile settings. */
  setLanguage(language: string): void {
    this.writeSettings({ ...this.readSettings(), language });
  }

  /** Removes the saved language while preserving other profile settings. */
  removeLanguage(): void {
    const settings = this.readSettings();
    if (!Object.hasOwn(settings, 'language')) return;
    delete settings['language'];
    this.writeSettings(settings);
  }

  /** Reads and validates saved settings, clearing malformed data and returning an empty object. */
  private readSettings(): Record<string, unknown> {
    const serialized = this.storage.getItem(ProfileSettingsService.STORAGE_KEY);
    if (serialized == null) return {};
    let readError: unknown;
    try {
      const settings: unknown = JSON.parse(serialized);
      if (isSettingsObject(settings)) {
        return settings;
      }
      readError = new TypeError('Profile settings must be a JSON object.');
    } catch (error) {
      readError = error;
    }
    console.warn('Could not read saved profile settings.', readError);
    this.storage.removeItem(ProfileSettingsService.STORAGE_KEY);
    return {};
  }

  /** Serializes profile settings and writes them to browser storage. */
  private writeSettings(settings: Record<string, unknown>): void {
    this.storage.setItem(ProfileSettingsService.STORAGE_KEY, JSON.stringify(settings));
  }
}
